/* eslint-disable react/jsx-props-no-spreading, react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import patternGenerator from '../../hooks/usePatternGenerator';
import useWindowSize from '../../hooks/useWindowSize';

import Title from '../partials/Title';
import Image from '../partials/Image';
import PatchedLink from '../partials/PatchedLink';
import BorderRandomizer from '../partials/BorderRandomizer';
import { yearBox } from '../icons/borders';

import * as AppPropTypes from '../../lib/PropTypes';

import styles from '../../../styles/blocks/year.scss';

const propTypes = {
    id: PropTypes.number.isRequired,
    year: PropTypes.string.isRequired,
    heading: PropTypes.string,
    text: AppPropTypes.text,
    link: PropTypes.shape({
        url: PropTypes.string,
        label: PropTypes.string,
    }),
    image: AppPropTypes.image,
    imageUrl: PropTypes.string,
    imageCredit: PropTypes.string,

    className: PropTypes.string,
};

const defaultProps = {
    heading: null,
    text: null,
    link: null,
    image: null,
    imageUrl: null,
    imageCredit: null,
    className: null,
};

const Year = ({ id, year, heading, text, link, image, imageUrl, imageCredit, className }) => {
    const shiftBlock = {
        farLeft: '0',
        left: '15px',
        none: '30px',
        right: '45px',
        farRight: '60px',
    };
    const yearPosition = {
        topLeft: { top: '-20px', left: '-5px' },
        topRight: { top: '-6px', left: '2px' },
        slightTop: { top: '-3px', left: '-2px' },
        middle: { top: '0', left: '1px' },
        middleAlt: { top: '1px', left: '0' },
        slightBottom: { top: '2px', left: '-3px' },
        bottom: { top: '4px', left: '2px' },
        farBottom: { top: '20px', left: '5px' },
    };
    const spaceContent = {
        small: '15px',
        medium: '20px',
        large: '25px',
    };
    const randomBlockShift = shiftBlock[patternGenerator(id, shiftBlock)];
    const randomYearPosition = yearPosition[patternGenerator(id * 2, yearPosition)];
    const randomSpaceContent = spaceContent[patternGenerator(id * 3, spaceContent)];
    const smallScreen = useWindowSize().width < 790;
    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.withImage]: image !== null,
                    [className]: className !== null,
                },
            ])}
        >
            <div
                className={styles.inner}
                style={smallScreen ? null : { marginLeft: randomBlockShift }}
            >
                <div className={styles.yearContainer}>
                    <div className={styles.yearBox}>
                        <BorderRandomizer
                            borders={yearBox}
                            seed={year * 4}
                            className={styles.yearBoxBorderContainer}
                            borderClassName={styles.yearBoxBorder}
                        />
                        <div
                            className={styles.year}
                            style={{
                                marginTop: randomYearPosition.top,
                                marginLeft: randomYearPosition.left,
                            }}
                        >
                            {year}
                        </div>
                    </div>
                    {heading !== null && smallScreen ? (
                        <Title
                            seed={heading}
                            text={heading}
                            borderPosition="top"
                            className={styles.heading}
                            borderClassName={styles.headingBorder}
                        />
                    ) : null}
                </div>
                <div
                    className={styles.content}
                    style={smallScreen ? null : { paddingLeft: randomSpaceContent }}
                >
                    {heading !== null && !smallScreen ? (
                        <Title
                            seed={heading}
                            text={heading}
                            borderPosition="top"
                            className={styles.heading}
                        />
                    ) : null}
                    {text !== null ? (
                        <div className={styles.text} dangerouslySetInnerHTML={{ __html: text }} />
                    ) : null}
                    {link !== null && link.url && link.label ? (
                        <PatchedLink
                            className={styles.link}
                            url={link.url}
                            label={link.label}
                            external
                        />
                    ) : null}
                    {image !== null ? (
                        <Image
                            {...image}
                            credits={imageCredit}
                            imageUrl={imageUrl}
                            className={styles.image}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

Year.propTypes = propTypes;
Year.defaultProps = defaultProps;

export default Year;
